import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar } from 'primereact/calendar';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import CustomDropdown from '../../custom/customDropdown';
import { AuthContext } from '../../Header/auth - context';
import './AnalizaNadoknada.css';

const H = 60 * 60 * 1000;

const hhmm = (ms) => {
    if (ms == null) return '—';
    const neg = ms < 0;
    const m = Math.abs(Math.round(ms / 60000));
    return `${neg ? '-' : ''}${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
};

/* Tahograf beleži u UTC, ali vozač i dispečer misle u LOKALNOM vremenu — kad
   piše da odmor ističe u 06:45, to je sat na zidu, ne UTC. Zato se prikazuje
   lokalno (isto kao referentni programi za analizu kartica). */
const dt = (v) => {
    if (!v) return '—';
    const d = new Date(v);
    if (isNaN(d.getTime())) return '—';
    return `${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()}. ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

/** Zvezdice: pune = koliko još sme, prazne = potrošeno. */
const Zvezdice = ({ ukupno, preostalo }) => (
    <span className="an-zvezde">
        {Array.from({ length: ukupno }, (_, i) => (
            <i key={i} className={`pi ${i < preostalo ? 'pi-star-fill an-zvezda-puna' : 'pi-star an-zvezda-prazna'}`} />
        ))}
    </span>
);

const PODTABOVI = ['LISTA', 'RAD', 'ODMOR', 'KM'];
/* GNSS tab se prikazuje samo ako kartica stvarno ima pozicije — GEN1 kartice ih
   uopšte nemaju, pa bi prazan tab bio zbunjujuć. */
const podtaboviZa = (data) => (data?.gnss?.length ? [...PODTABOVI, 'GNSS'] : PODTABOVI);

/* Period i vozač se preuzimaju sa zajedničkog filtera ostalih tabova, pa se
   analiza pokreće sama čim se pređe na ovaj tab. Režimi 28/56 dana ostaju
   lokalni jer postoje samo ovde. */
const AnalizaNadoknada = ({ cards, dateFrom: propOd, dateTo: propDo, selectedCard: propVozac, onFilterChange }) => {
    const { t } = useTranslation();
    const auth = useContext(AuthContext);

    const [vozac, setVozac] = useState(null);
    const [dateFrom, setDateFrom] = useState(new Date(Date.now() - 28 * 86400000));
    const [dateTo, setDateTo] = useState(new Date());
    const [rezim, setRezim] = useState('PERIOD');      // PERIOD | D28 | D56
    const [podtab, setPodtab] = useState('LISTA');
    const [data, setData] = useState(null);
    const [greska, setGreska] = useState(null);
    const [load, setLoad] = useState(false);
    const [nadoknadaCek, setNadoknadaCek] = useState(false);

    const [autoPokreni, setAutoPokreni] = useState(false);

    const toISO = (d) => (d instanceof Date
        ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
        : (d || ''));

    /* ── ulaz: preuzmi period i vozača sa zajedničkog filtera ───────────────
       Poređenje ide po VREDNOSTI (ISO), ne po referenci: parent vraća nove Date
       objekte, pa bi poređenje po referenci napravilo petlju ulaz→izlaz→ulaz. */
    const zadnjiFilter = useRef('');

    useEffect(() => {
        const potpis = `${toISO(propOd)}|${toISO(propDo)}|${propVozac ?? ''}`;
        if (potpis === zadnjiFilter.current) return;
        zadnjiFilter.current = potpis;

        if (propOd) setDateFrom(propOd);
        if (propDo) setDateTo(propDo);
        if (propVozac != null) { setVozac(propVozac); setAutoPokreni(true); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [propOd, propDo, propVozac]);

    useEffect(() => {
        if (!autoPokreni || !vozac) return;
        setAutoPokreni(false);
        prikazi();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoPokreni, vozac]);

    /* ── izlaz: 28/56 dana pomeraju zajednički period na isti opseg ───────── */
    const posaljiFilter = (od, doo) => {
        if (!onFilterChange || !od || !doo) return;
        // upiši potpis unapred da povratak istog filtera ne pokrene analizu opet
        zadnjiFilter.current = `${toISO(new Date(od))}|${toISO(new Date(doo))}|${vozac ?? ''}`;
        onFilterChange({ dateFrom: new Date(od), dateTo: new Date(doo), selectedCard: vozac });
    };

    const prikazi = () => {
        if (!vozac) { setGreska(t('an.izaberiVozaca')); setData(null); return; }
        setLoad(true); setGreska(null);
        const body = { employeeId: vozac };
        if (rezim === 'PERIOD') { body.dateFrom = toISO(dateFrom); body.dateTo = toISO(dateTo); }
        else body.poslednjihDana = rezim === 'D28' ? 28 : 56;

        fetch(process.env.REACT_APP_BACKEND_URL + '/tacho/driverAnalysis', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
            body: JSON.stringify(body),
        })
            .then(r => (r.ok ? r.json() : Promise.reject(r)))
            .then(d => {
                if (!d.stavke || d.stavke.length === 0) { setGreska(t('an.nemaPodataka')); setData(null); return; }
                /* Za režime 28/56 dana stvarni opseg zna samo backend (računa se
                   od poslednje aktivnosti) — njime se osvežava zajednički filter,
                   da ostali tabovi pokažu tačno te dane. */
                if (rezim !== 'PERIOD') {
                    const od = d.stavke[0]?.od;
                    const doo = d.stavke[d.stavke.length - 1]?.do;
                    if (od && doo) {
                        setDateFrom(new Date(od));
                        setDateTo(new Date(doo));
                        posaljiFilter(od, doo);
                    }
                }
                /* Datum očitavanja se ne ponavlja kao upozorenje — stoji u traci
                   "Važi" iznad analize. Poruka ostaje samo kad datum očitavanja
                   uopšte ne postoji, jer se tada ne zna dokle podaci važe. */
                if (!d.svezi && !d.ocitanoDana) setGreska(t('an.zastareliPodaci'));
                setData(d);
            })
            .catch(() => { setGreska(t('an.greskaAnalize')); setData(null); })
            .finally(() => setLoad(false));
    };

    // režim izbora perioda: samo jedan od tri načina
    const postaviRezim = (novi) => setRezim(novi);

    const stavke = useMemo(() => (data?.stavke || []).slice().reverse(), [data]);
    const s = data?.sumarno;
    const L = data?.limiti;

    return (
        <div className="an-root">
            {/* ── Filteri ── */}
            <div className="kazne-izv-filters">
                <div className="kazne-izv-filter-row">
                    <label className="kazne-izv-label">{t('ture.vozac')}</label>
                    <CustomDropdown
                        value={vozac} options={cards || []} optionLabel="label"
                        onChange={e => setVozac(e.target.value)}
                        placeholder={t('an.izaberiVozaca')} className="kazne-izv-dropdown"
                    />

                    <div className="an-rezim">
                        <span className="an-rezim-item">
                            <Checkbox inputId="rPeriod" checked={rezim === 'PERIOD'} onChange={() => postaviRezim('PERIOD')} />
                            <label htmlFor="rPeriod">{t('an.period')}</label>
                        </span>
                        <span className="an-rezim-item">
                            <Checkbox inputId="r28" checked={rezim === 'D28'} onChange={() => postaviRezim('D28')} />
                            <label htmlFor="r28">{t('an.poslednjih28')}</label>
                        </span>
                        <span className="an-rezim-item">
                            <Checkbox inputId="r56" checked={rezim === 'D56'} onChange={() => postaviRezim('D56')} />
                            <label htmlFor="r56">{t('an.poslednjih56')}</label>
                        </span>
                    </div>

                    {rezim === 'PERIOD' && (
                        <>
                            <Calendar value={dateFrom} onChange={e => setDateFrom(e.value)} showIcon dateFormat="dd.mm.yy" mask="99.99.9999" />
                            <Calendar value={dateTo} onChange={e => setDateTo(e.value)} showIcon dateFormat="dd.mm.yy" mask="99.99.9999" />
                        </>
                    )}

                    <Button label={t('mk.prikazi')} icon="pi pi-search" onClick={prikazi} loading={load} className="p-button p-button-primary" />
                </div>
            </div>

            {greska && <div className={`an-poruka${data ? ' an-poruka-warn' : ''}`}>{greska}</div>}

            {data && (
                <>
                    {/* "Važi" traka — do kada analiza važi, tj. kada je kartica
                        očitana. Poslednja aktivnost nije isto: kartica se očitava
                        i danima posle poslednje vožnje, a odmor u međuvremenu teče. */}
                    <div className="an-vazi">
                        {t('an.vazi')}: {dt(data.ocitanoDana || data.poslednjaAktivnost)}
                        {data.vozac ? ` · ${data.vozac}` : ''}
                    </div>

                    <div className="kazne-wh-tabs an-podtabovi">
                        {podtaboviZa(data).map(p => (
                            <button key={p}
                                className={'kazne-wh-tab' + (podtab === p ? ' kazne-wh-tab--active' : '')}
                                onClick={() => setPodtab(p)}>
                                {t(`an.tab_${p}`)}
                            </button>
                        ))}
                    </div>

                    {/* Skrol pocinje ovde — filteri, traka Vazi i podtabovi ostaju iznad */}
                    <div className="an-sadrzaj">
                    {/* ── LISTA AKTIVNOSTI ── */}
                    {podtab === 'LISTA' && (
                        <div className="an-lista">
                            {stavke.map((x, i) => (
                                <div key={i} className={`an-stavka ${x.tip === 'ODMOR' ? 'an-odmor' : 'an-rad'}`}>
                                    <div className="an-stavka-head">
                                        {dt(x.od)} – {dt(x.do)} <b>({hhmm(x.trajanje)})</b>
                                    </div>
                                    {x.tip === 'ODMOR' ? (
                                        <div className="an-stavka-body">
                                            <span>{x.nazivOdmora} ({hhmm(x.trajanje)})</span>
                                            {/* Nadoknada se u listi prikazuje po ISTOM pravilu kao u
                                                tabu ODMOR: dok traje rok (čl. 8(6b), treća nedelja
                                                posle skraćenja) obaveza je aktivna, posle toga se
                                                vidi kao istekla. Bez ovoga bi lista pokazivala
                                                obaveze kojih u tabu ODMOR odavno nema. */}
                                            {x.nadoknada > 0 && (
                                                x.nadoknadaVazi ? (
                                                    <span className="an-nadoknada">
                                                        {t('an.nadoknada')}: {hhmm(x.nadoknada)}
                                                        {x.nadoknadaRok && ` · ${t('an.rokDo')} ${dt(x.nadoknadaRok)}`}
                                                    </span>
                                                ) : (
                                                    <span className="an-nadoknada an-nadoknada-istekla"
                                                        title={t('an.nadoknadaIsteklaOpis')}>
                                                        {t('an.nadoknada')}: {hhmm(x.nadoknada)} · {t('an.rokIstekao')}
                                                    </span>
                                                )
                                            )}
                                        </div>
                                    ) : (
                                        <div className="an-stavka-body">
                                            <span title={t('taho.leg_drive')}>🕐 {hhmm(x.voznja)}</span>
                                            <span title={t('taho.leg_work')}>🔨 {hhmm(x.rad)}</span>
                                            <span title={t('taho.leg_available')}>▨ {hhmm(x.raspolozivost)}</span>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* ── RAD ── */}
                    {podtab === 'RAD' && s && L && (
                        <div className="an-panel">
                            <div className="an-sekcija">{t('an.vremeVoznje')}</div>
                            {/* prikazuje se STVARNA odvožena vožnja danas, ne sam limit */}
                            <div className="an-red"><span>{t('an.dnevniMax')}</span>
                                <span>{hhmm(s.voznjaDanas || 0)} / {hhmm(L.dnevnaVoznjaProduzena)}</span></div>
                            <div className="an-red">
                                <span>{t('an.produzeniDan')}</span>
                                <Zvezdice ukupno={L.produzenihNedeljno} preostalo={s.produzeniPreostali} />
                            </div>
                            <div className="an-red"><span>{t('an.nedeljnoMax')}</span><span>{hhmm(s.voznjaNedeljno)} / {hhmm(L.nedeljnaVoznja)}</span></div>
                            <div className="an-red"><span>{t('an.dvonedeljnoMax')}</span><span>{hhmm(s.voznjaDvonedeljno)} / {hhmm(L.dvonedeljnaVoznja)}</span></div>

                            {/* Radno vreme = vožnja + ostali rad (Direktiva 2002/15/EZ);
                                raspoloživost se ne računa. Prosek je za 16 nedelja. */}
                            <div className="an-sekcija">{t('an.radnoVreme')}</div>
                            <div className="an-red"><span>{t('an.radnoNedeljnoMax')}</span>
                                <span>{hhmm(s.radnoVremeNedeljno || 0)} / {hhmm(L.radnoVremeNedeljno)}</span></div>
                            <div className="an-red"><span>{t('an.radnoProsekMax')}</span>
                                <span>{hhmm(s.radnoVremeProsek || 0)} / {hhmm(L.radnoVremeProsek)}</span></div>
                            <div className="an-red"><span>{t('an.radnoDnevni')}</span>
                                <span>{hhmm(s.radnoVremeDanas || 0)}</span></div>

                            <div className="an-sekcija">{t('an.sledeciDnevniOdmor')}</div>
                            {/* rok se računa u motoru: kraj prethodnog odmora + 24h − 9h */}
                            <div className="an-red"><span>{t('an.najkasnijeDo')}</span>
                                <span>{dt(s.sledeciDnevniOdmorDo)}</span></div>
                            <div className="an-red"><span>{t('an.trajanjeMozeSkraceni')}</span><span>9 h</span></div>
                            <div className="an-red">
                                <span>{t('an.smanjenaMax')}</span>
                                <Zvezdice ukupno={L.skracenihIzmedjuNedeljnih} preostalo={s.skraceniPreostali} />
                            </div>

                            <div className="an-sekcija">{t('an.sledeciNedeljniOdmor')}</div>
                            <div className="an-red"><span>{t('an.najkasnijeDo')}</span>
                                <span>{dt(s.sledeciNedeljniOdmorDo)}</span></div>
                            <div className="an-red"><span>{t('an.trajanjeMozeSkraceni')}</span><span>24 h</span></div>

                            {/* Čl. 8(6): u dve uzastopne nedelje mora postojati bar
                                jedan REDOVNI nedeljni odmor od 45h */}
                            <div className="an-sekcija">{t('an.sledeciRedovniNedeljniOdmor')}</div>
                            <div className="an-red"><span>{t('an.najkasnijeDo')}</span>
                                <span>{dt(s.sledeciRedovniNedeljniDo)}</span></div>
                            <div className="an-red"><span>{t('an.trajanje')}</span><span>45 h</span></div>
                        </div>
                    )}

                    {/* ── ODMOR ── */}
                    {podtab === 'ODMOR' && (
                        <div className="an-panel">
                            <div className="an-sekcija">{t('an.trenutniPeriodOdmora')}</div>
                            <div className="an-red"><span>{t('an.poceo')}</span><span>{dt(data.poslednjiOdmor?.od)}</span></div>

                            <div className="an-sekcija">{t('an.mogucaVremenaZavrsetka')}</div>
                            {data.poslednjiOdmor && [
                                { k: 'smanjenDnevni', h: 9 }, { k: 'redovniDnevni', h: 11 },
                                { k: 'smanjenNedeljni', h: 24 }, { k: 'redovniNedeljni', h: 45 },
                            ].map(o => {
                                const kraj = new Date(new Date(data.poslednjiOdmor.od).getTime() + o.h * H);
                                const dostignut = Date.now() >= kraj.getTime();
                                return (
                                    <div key={o.k} className="an-red">
                                        <span>{t(`an.${o.k}`)} ({o.h} {t('an.sati')})</span>
                                        <span className={dostignut ? 'an-zeleno' : ''}>{dt(kraj)}</span>
                                    </div>
                                );
                            })}

                            {/* Nadoknada — pojavljuje se samo ako postoji nenadoknađeni skraćeni nedeljni odmor */}
                            {data.nadoknade?.length > 0 && (
                                <>
                                    <div className="an-sekcija">{t('an.nadoknada')}</div>
                                    <div className="an-red">
                                        <span className="an-nadoknada-red">
                                            <Checkbox inputId="nad" checked={nadoknadaCek} onChange={e => setNadoknadaCek(e.checked)} />
                                            <label htmlFor="nad">{t('an.nadoknadaTrajanje')}</label>
                                        </span>
                                        <span>ΔT = {hhmm(data.nadoknade[0].nadoknada)}</span>
                                    </div>
                                    {nadoknadaCek && data.poslednjiOdmor && (
                                        <div className="an-red an-planiran">
                                            <span>{t('an.planiranPolazak')}</span>
                                            <span className="an-zeleno">
                                                {dt(new Date(new Date(data.poslednjiOdmor.od).getTime()
                                                    + 45 * H + data.nadoknade[0].nadoknada))}
                                            </span>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}

                    {/* ── KILOMETRAŽE ── */}
                    {podtab === 'KM' && (
                        <DataTable value={data.kilometraza || []} paginator rows={25} size="small"
                            emptyMessage={t('common.noData')} className="turePregledTable stavke-tabela" scrollable scrollHeight="50vh">
                            <Column field="Datum" header={t('taho.datum')} sortable style={{ minWidth: '120px' }} />
                            <Column field="Registracija" header={t('ture.registracija')} sortable style={{ minWidth: '130px' }} />
                            <Column field="KmOd" header={t('an.kmOd')} sortable style={{ minWidth: '110px' }} body={(r) => (r.KmOd == null ? '—' : r.KmOd)} />
                            <Column field="KmDo" header={t('an.kmDo')} sortable style={{ minWidth: '110px' }} body={(r) => (r.KmDo == null ? '—' : r.KmDo)} />
                            <Column field="Predjeno" header={t('an.predjeno')} sortable style={{ minWidth: '120px' }}
                                body={(r) => (r.Predjeno == null ? '—' : <b>{r.Predjeno} km</b>)} />
                        </DataTable>
                    )}

                    {/* ── GNSS POZICIJE (samo GEN2 kartice) ── */}
                    {podtab === 'GNSS' && (
                        <>
                            <p className="an-napomena">{t('an.gnssObjasnjenje')}</p>
                            <DataTable value={data.gnss || []} paginator rows={25} size="small"
                                emptyMessage={t('common.noData')} className="turePregledTable stavke-tabela" scrollable scrollHeight="50vh">
                                <Column header={t('an.gnssVreme')} sortable sortField="EntryTime" style={{ minWidth: '150px' }}
                                    body={(r) => dt(r.EntryTime)} />
                                <Column header={t('an.gnssKoordinate')} style={{ minWidth: '180px' }}
                                    body={(r) => (r.Latitude == null ? '—'
                                        : `${Number(r.Latitude).toFixed(5)}, ${Number(r.Longitude).toFixed(5)}`)} />
                                <Column header={t('an.gnssTacnost')} sortable sortField="AccuracyKm" style={{ minWidth: '110px' }}
                                    body={(r) => (r.AccuracyKm == null ? '—' : `± ${r.AccuracyKm} km`)} />
                                <Column header={t('an.gnssOdometar')} sortable sortField="OdometerKm" style={{ minWidth: '120px' }}
                                    body={(r) => (r.OdometerKm == null ? '—' : `${r.OdometerKm} km`)} />
                                <Column header={t('an.gnssMapa')} style={{ minWidth: '100px' }}
                                    body={(r) => (r.Latitude == null ? '—' : (
                                        <a href={`https://www.google.com/maps?q=${r.Latitude},${r.Longitude}`}
                                            target="_blank" rel="noreferrer">{t('an.gnssOtvori')}</a>
                                    ))} />
                            </DataTable>
                        </>
                    )}
                    </div>
                </>
            )}
        </div>
    );
};

export default AnalizaNadoknada;
